import { createFileRoute } from "@tanstack/react-router";
import { newsDetailQueryOptions } from "@/lib/client/apex-news";
import { newsReactionsQueryOptions } from "@/lib/client/news-reaction";
import {
  createStructuredDataScript,
  generateNewsArticleStructuredData,
  seo,
  toAbsoluteUrl,
} from "@/lib/utils/seo";
import { getNewsDisplayLabel, toNewsAssetUrlForRender } from "@/lib/utils/apex-news";
import { NewsDetailPage } from "@/features/news/article/news-detail-page";

export const Route = createFileRoute("/_index/news/$slug")({
  component: RouteComponent,
  staticData: {
    title: "新闻详情",
  },
  async loader({ context, params }) {
    const detailData = await context.queryClient.ensureQueryData(
      newsDetailQueryOptions(params.slug),
    );
    await context.queryClient.ensureQueryData(
      newsReactionsQueryOptions([{ targetType: "news", targetId: detailData.detail.id }]),
    );
    return detailData;
  },
  head: async ({ loaderData }) => {
    const data = await Promise.resolve(
      loaderData as
        | {
            detail: {
              title: string;
              summary: string | null;
              slug: string;
              coverImage: string | null;
              sourceUrl: string | null;
              sourcePlatform?: string | null;
              sourceAuthorHandle?: string | null;
              publishingDate: Date | string;
            };
            previous: { slug: string } | null;
            next: { slug: string } | null;
          }
        | undefined,
    );

    if (!data?.detail) {
      return {
        meta: [
          ...seo({
            title: "新闻不存在 | CN Apex Club",
            description: "该篇新闻不存在或已被删除。",
            type: "article",
            url: toAbsoluteUrl("/news"),
            canonical: toAbsoluteUrl("/news"),
            noindex: true,
            nofollow: true,
          }),
        ],
      };
    }

    const detail = data.detail;
    const displayTitle = getNewsDisplayLabel({
      sourcePlatform: detail.sourcePlatform,
      sourceAuthorHandle: detail.sourceAuthorHandle,
      title: detail.title,
      fallback: "新闻详情",
    });
    const canonical = toAbsoluteUrl(`/news/${detail.slug}`);
    const rawDescription = detail.summary || "查看 Apex Legends 官方新闻详情、更新说明与活动内容。";
    const description = rawDescription.replace(/\s+/g, " ").trim();
    const image = toAbsoluteUrl(
      toNewsAssetUrlForRender(detail.coverImage, {
        referer: detail.sourceUrl,
      }) || "/favicon.png",
    );
    const publishedTime = detail.publishingDate
      ? new Date(detail.publishingDate).toISOString()
      : undefined;
    const links = [];

    if (data.previous?.slug) {
      links.push({ rel: "prev", href: toAbsoluteUrl(`/news/${data.previous.slug}`) });
    }

    if (data.next?.slug) {
      links.push({ rel: "next", href: toAbsoluteUrl(`/news/${data.next.slug}`) });
    }

    return {
      meta: [
        ...seo({
          title: `${displayTitle} | CN Apex Club`,
          description,
          image,
          type: "article",
          url: canonical,
          canonical,
          publishedTime,
          author: "CN Apex Club",
        }),
      ],
      links,
      scripts: [
        createStructuredDataScript(
          generateNewsArticleStructuredData({
            title: displayTitle,
            description,
            image,
            url: canonical,
            publishedTime,
          }),
        ),
      ],
    };
  },
});

function RouteComponent() {
  const { slug } = Route.useParams();
  const { user } = Route.useRouteContext();
  return <NewsDetailPage articleSlug={slug} currentUser={user} />;
}
